/**
 * Query decomposer — ARCHITECTURE.md section 5.4.2-5.4.3.
 *
 * Parses an analyst's free-text query into a structured ScopeContract
 * using the LLM (Sonnet), constrained by the desk config.
 *
 * The decomposed result is returned to the analyst for confirmation
 * before any collection begins. The desk config acts as a hard fence
 * on geographic bounds, sources, and entity types.
 */

import type { DeskConfig } from '@/config/desk-config';
import type { LLMAdapter } from '@/intelligence/ai/llm-adapter';
import { extractJSON } from '@/intelligence/ai/llm-adapter';
import type { AICompletionRequest } from '@/intelligence/ai/providers/base-provider';
import { executeWithFallback } from '@/intelligence/ai/fallback-chain';
import type { ScopeContract } from './types';

// ---------------------------------------------------------------------------
// Decomposition result
// ---------------------------------------------------------------------------

export interface DecompositionResult {
  scopeContract: ScopeContract;
  tokensUsed: number;
  model: string;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const VALID_ENTITY_TYPES = new Set([
  'PERSON', 'ORGANIZATION', 'LOCATION', 'ASSET', 'EVENT', 'INDICATOR', 'SOURCE',
]);

/**
 * Validate and sanitize a decomposed scope contract.
 * Ensures all fields match expected types, applies desk config constraints.
 */
function validateAndSanitize(
  raw: Record<string, unknown>,
  deskConfig: DeskConfig,
): ScopeContract | null {
  // Validate subject
  const subject = raw.subject as Record<string, unknown> | undefined;
  if (!subject || typeof subject !== 'object') return null;

  const subjectType = String(subject.type ?? '').toUpperCase();
  if (!VALID_ENTITY_TYPES.has(subjectType)) return null;

  const description = String(subject.description ?? '');
  if (!description) return null;

  // Match watchlist entries
  const rawWatchlistMatches = Array.isArray(subject.watchlistMatches)
    ? (subject.watchlistMatches as unknown[]).map(String)
    : [];

  // Validate against actual desk watchlist
  const validWatchlistNames = new Set(deskConfig.entityWatchlists.map(w => w.name));
  const watchlistMatches = rawWatchlistMatches.filter(name => validWatchlistNames.has(name));

  // Validate behavior indicators
  const rawIndicators = Array.isArray(raw.behaviorIndicators)
    ? (raw.behaviorIndicators as Array<Record<string, unknown>>)
    : [];

  const behaviorIndicators = rawIndicators
    .filter(bi => bi && typeof bi.type === 'string' && typeof bi.threshold === 'string')
    .map(bi => ({
      type: String(bi.type),
      threshold: String(bi.threshold),
    }));

  // Geographic bounds — ALWAYS use desk config (hard fence)
  const bbox = deskConfig.geographicFocus.primary.coords;

  // Temporal bounds — default 7 days from now
  let temporalStart: string;
  let temporalEnd: string;
  let temporalSource = 'desk_config_default_7d';

  const rawTemporal = raw.temporalBounds as Record<string, unknown> | undefined;
  if (rawTemporal && typeof rawTemporal === 'object') {
    const parsedStart = rawTemporal.start ? new Date(String(rawTemporal.start)) : null;
    const parsedEnd = rawTemporal.end ? new Date(String(rawTemporal.end)) : null;

    if (parsedStart && !isNaN(parsedStart.getTime()) && parsedEnd && !isNaN(parsedEnd.getTime())) {
      temporalStart = parsedStart.toISOString();
      temporalEnd = parsedEnd.toISOString();
      if (typeof rawTemporal.source === 'string') {
        temporalSource = rawTemporal.source;
      }
    } else {
      // Default: 7 days ago to now
      const end = new Date();
      const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
      temporalStart = start.toISOString();
      temporalEnd = end.toISOString();
    }
  } else {
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    temporalStart = start.toISOString();
    temporalEnd = end.toISOString();
  }

  // Exclusions
  const exclusions = Array.isArray(raw.exclusions)
    ? (raw.exclusions as unknown[]).filter(e => typeof e === 'string').map(String)
    : [];

  return {
    subject: {
      type: subjectType,
      subtype: subject.subtype ? String(subject.subtype) : undefined,
      description,
      watchlistMatches,
    },
    behaviorIndicators,
    geographicBounds: {
      source: 'desk_config_primary_aoi',
      bbox,
      override: false,
    },
    temporalBounds: {
      start: temporalStart,
      end: temporalEnd,
      source: temporalSource,
    },
    exclusions,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const MAX_RETRIES = 3;

/**
 * Decompose a free-text query into a structured scope contract.
 *
 * Uses the LLM to parse the query, then validates and constrains
 * the output with the desk config. Returns the scope contract
 * for analyst confirmation.
 *
 * @param rawQuery - The analyst's free-text investigation query
 * @param deskConfig - The active desk configuration (hard constraint fence)
 * @param _adapter - The LLM adapter (unused — we call executeWithFallback directly for decomposition)
 */
export async function decomposeQuery(
  rawQuery: string,
  deskConfig: DeskConfig,
  _adapter?: LLMAdapter,
): Promise<DecompositionResult> {
  const { buildQueryDecompositionPrompt } = await import('@/intelligence/ai/prompts/extraction');
  const { system, user } = buildQueryDecompositionPrompt(rawQuery, deskConfig);

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const request: AICompletionRequest = {
      systemPrompt: system,
      prompt: attempt === 0
        ? user
        : user + '\n\nPrevious attempt returned invalid JSON. Return ONLY valid JSON.',
      model: 'claude-sonnet-5',
      // Adaptive thinking counts toward max_tokens on Sonnet 5 — 2048 could
      // truncate the JSON mid-thought. Effort 'medium': decomposition is the
      // quality anchor for the whole investigation but runs only once.
      maxTokens: 8192,
      temperature: 0.1,
      effort: 'medium',
    };

    const response = await executeWithFallback(request, {
      providers: ['anthropic', 'openrouter', 'ollama'],
      timeout: 60_000,
      retries: 1,
    });

    const jsonText = extractJSON(response.text);
    if (!jsonText) {
      lastError = new Error(`Attempt ${attempt + 1}: No valid JSON in decomposition response`);
      continue;
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(jsonText) as Record<string, unknown>;
    } catch {
      lastError = new Error(`Attempt ${attempt + 1}: JSON parse failed`);
      continue;
    }

    const scopeContract = validateAndSanitize(parsed, deskConfig);
    if (!scopeContract) {
      lastError = new Error(`Attempt ${attempt + 1}: Decomposition failed validation`);
      continue;
    }

    return {
      scopeContract,
      tokensUsed: response.tokensUsed ?? 0,
      model: response.model,
    };
  }

  // After all retries failed, construct a minimal valid scope contract
  // from what we know deterministically (empty is better than crashing)
  const end = new Date();
  const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);

  console.warn(
    `[Sentinel] Query decomposition failed after ${MAX_RETRIES} attempts. Using minimal scope.`,
    lastError?.message,
  );

  return {
    scopeContract: {
      subject: {
        type: 'EVENT',
        description: rawQuery,
        watchlistMatches: [],
      },
      behaviorIndicators: [],
      geographicBounds: {
        source: 'desk_config_primary_aoi',
        bbox: deskConfig.geographicFocus.primary.coords,
        override: false,
      },
      temporalBounds: {
        start: start.toISOString(),
        end: end.toISOString(),
        source: 'desk_config_default_7d',
      },
      exclusions: [],
    },
    tokensUsed: 0,
    model: 'fallback',
  };
}
